import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { ApiError, createApiRequest } from '../../utils/apiUtils';
import { resizeImage } from '../Menu/menuApi';
import {
  BusinessHourSlot,
  PaymentSetting,
  ReservationImageKind,
  ReservationInquiry,
  ReservationInquiryInput,
  ReservationMailPreview,
  ReservationMailPreviewType,
  ReservationMenu,
  ReservationMenuInput,
  ReservationSeatType,
  ReservationSeatTypeInput,
  ReservationConflict,
  ReservationSetting,
  ReservationStoreImages,
  ReservationStoreInfo,
  SpecialDay,
  StripeConnectionStatus,
} from '../../types/reservation';

/** 予約設定ハブ（受付設定/席・在庫/営業時間/特定日/コース/質問/決済方針/通知メール+店舗情報）の API 群 */

/** BE が未登録リソースに 404 を返す設定系 GET の判定（未登録＝初期値表示で保存＝新規作成の upsert 運用） */
const isNotFound = (error: unknown): boolean =>
  error instanceof Error && error.message.includes('status: 404');

/**
 * 設定変更 API の失敗を ReservationConflict に正規化する（B4/B5）。
 * ApiError から status とパース済み本文（affectedCount 等）を取り出す。
 * ApiError でない（ネットワークエラー等）場合は status 無しでメッセージのみ返す。
 */
const toReservationConflict = (error: unknown): ReservationConflict => {
  if (error instanceof ApiError) {
    const body = error.parsedBody as { message?: unknown; affectedCount?: unknown } | null;
    const message =
      body && typeof body.message === 'string' ? body.message : error.message;
    const affectedCount =
      body && typeof body.affectedCount === 'number' ? body.affectedCount : undefined;
    return { status: error.status, message, affectedCount };
  }
  return { message: error instanceof Error ? error.message : String(error ?? '') };
};

/** 受付設定の初期値（未登録店舗の初回表示用。保存すると BE の upsert で作成される） */
export const DEFAULT_RESERVATION_SETTING: ReservationSetting = {
  approvalMode: 'AUTO',
  slotIntervalMinutes: 30,
  maxPartiesPerSlot: 0,
  maxPeoplePerSlot: 0,
  minPartySize: 1,
  maxPartySize: 8,
  acceptanceFromDays: 0,
  acceptanceUntilDays: 92,
  acceptanceCutoffMinutes: 120,
  reminderOffsetHours: 24,
  requestApprovalSlaHours: 0,
  capacityMode: 'AGGREGATE',
  defaultStayMinutes: 120,
  // フィードバック1: 未登録店舗の初期値は 48 時間（BE の upsert 既定とも揃える）
  selfCancelDeadlineHours: 48,
  phoneRequired: false,
  mailCustomMessage: null,
  publicPageEnabled: true,
  // フィードバック4: 未登録店舗の初期値（BE の migration 022 既定と揃える）
  courseSelectionRequired: false,
  taxRatePercent: 10,
};

// ---------- 受付設定 ----------

/**
 * 受付設定の取得結果。`persisted=false` は BE が 404 を返した「一度も保存されていない店舗」で、
 * この場合 `setting` は DEFAULT_RESERVATION_SETTING（初期値）になる。
 * 未保存店舗は公開予約の受付が開始できない（availability/投稿が 400）ため、
 * UI 側で保存を促す警告と保存導線を出す判定に使う。
 */
export interface FetchReservationSettingResult {
  setting: ReservationSetting;
  /** true=200（保存済み） / false=404（未保存・初期値） */
  persisted: boolean;
}

export const fetchReservationSetting = createAsyncThunk<FetchReservationSettingResult, string>(
  'reservationSettings/fetchReservationSetting',
  async (storeId, { rejectWithValue }) => {
    try {
      const setting = await createApiRequest<ReservationSetting>(
        `/stores/${storeId}/reservation-setting`,
        {
          method: 'GET',
          requiresAuth: true,
        },
      );
      return { setting, persisted: true };
    } catch (error) {
      // 未登録店舗は 404 → 初期値でフォームを出し、保存（PUT=upsert）で作成させる。
      // persisted=false を保持し、UI で未保存警告と保存ボタンを表示する。
      if (isNotFound(error)) return { setting: DEFAULT_RESERVATION_SETTING, persisted: false };
      return rejectWithValue((error as Error).message);
    }
  },
);

export const saveReservationSetting = createAsyncThunk<
  ReservationSetting,
  { storeId: string; setting: ReservationSetting }
>('reservationSettings/saveReservationSetting', async ({ storeId, setting }, { rejectWithValue }) => {
  try {
    await createApiRequest(`/stores/${storeId}/reservation-setting`, {
      method: 'PUT',
      requiresAuth: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(setting),
    });
    return setting;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// ---------- 席タイプ（席・在庫設定） ----------

export const fetchSeatTypes = createAsyncThunk<ReservationSeatType[], string>(
  'reservationSettings/fetchSeatTypes',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<ReservationSeatType[]>(
        `/stores/${storeId}/reservation-seat-types`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const createSeatType = createAsyncThunk<
  void,
  { storeId: string; input: ReservationSeatTypeInput }
>('reservationSettings/createSeatType', async ({ storeId, input }, { rejectWithValue, dispatch }) => {
  try {
    await createApiRequest<{ id: string }>(`/stores/${storeId}/reservation-seat-types`, {
      method: 'POST',
      requiresAuth: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    dispatch(fetchSeatTypes(storeId));
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const updateSeatType = createAsyncThunk<
  void,
  { storeId: string; seatTypeId: string; input: ReservationSeatTypeInput }
>(
  'reservationSettings/updateSeatType',
  async ({ storeId, seatTypeId, input }, { rejectWithValue, dispatch }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservation-seat-types/${seatTypeId}`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      dispatch(fetchSeatTypes(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const deleteSeatType = createAsyncThunk<void, { storeId: string; seatTypeId: string }>(
  'reservationSettings/deleteSeatType',
  async ({ storeId, seatTypeId }, { rejectWithValue, dispatch }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservation-seat-types/${seatTypeId}`, {
        method: 'DELETE',
        requiresAuth: true,
      });
      dispatch(fetchSeatTypes(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// ---------- 営業時間 ----------

export const fetchBusinessHours = createAsyncThunk<BusinessHourSlot[], string>(
  'reservationSettings/fetchBusinessHours',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<BusinessHourSlot[]>(`/stores/${storeId}/business-hours`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const saveBusinessHours = createAsyncThunk<
  void,
  {
    storeId: string;
    slots: { dayOfWeek: number; open: string; close: string }[];
    /**
     * B5: 影響予約ありでも変更を続行する。省略/false で影響予約があるとBEが409を返す。
     * true を付けて再送すると置換を実行する。
     */
    confirm?: boolean;
  },
  { rejectValue: ReservationConflict }
>('reservationSettings/saveBusinessHours', async ({ storeId, slots, confirm }, { rejectWithValue, dispatch }) => {
  try {
    await createApiRequest(`/stores/${storeId}/business-hours`, {
      method: 'PUT',
      requiresAuth: true,
      headers: { 'Content-Type': 'application/json' },
      // confirm は optional 後方互換。未指定時は送らず従来挙動（BEが影響有無で判定）
      body: JSON.stringify(confirm === undefined ? { slots } : { slots, confirm }),
    });
    dispatch(fetchBusinessHours(storeId));
  } catch (error) {
    return rejectWithValue(toReservationConflict(error));
  }
});

// ---------- 特定日 ----------

export const fetchSpecialDays = createAsyncThunk<
  SpecialDay[],
  { storeId: string; from: string; to: string }
>('reservationSettings/fetchSpecialDays', async ({ storeId, from, to }, { rejectWithValue }) => {
  try {
    return await createApiRequest<SpecialDay[]>(
      `/stores/${storeId}/special-days?from=${from}&to=${to}`,
      { method: 'GET', requiresAuth: true },
    );
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const upsertSpecialDay = createAsyncThunk<
  void,
  {
    storeId: string;
    date: string;
    isClosed: boolean;
    hours: { open: string; close: string }[];
  },
  { rejectValue: ReservationConflict }
>(
  'reservationSettings/upsertSpecialDay',
  async ({ storeId, date, isClosed, hours }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/special-days`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, isClosed, hours }),
      });
    } catch (error) {
      // B4: 臨時休業化でその日に予約があると 409(affectedCount)。ステータスを保持して返す。
      return rejectWithValue(toReservationConflict(error));
    }
  },
);

export const deleteSpecialDay = createAsyncThunk<
  void,
  { storeId: string; specialDayId: string }
>('reservationSettings/deleteSpecialDay', async ({ storeId, specialDayId }, { rejectWithValue }) => {
  try {
    await createApiRequest(`/stores/${storeId}/special-days/${specialDayId}`, {
      method: 'DELETE',
      requiresAuth: true,
    });
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// ---------- コースメニュー ----------

export const fetchReservationMenus = createAsyncThunk<ReservationMenu[], string>(
  'reservationSettings/fetchReservationMenus',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<ReservationMenu[]>(`/stores/${storeId}/reservation-menus`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const createReservationMenu = createAsyncThunk<
  void,
  { storeId: string; input: ReservationMenuInput }
>(
  'reservationSettings/createReservationMenu',
  async ({ storeId, input }, { rejectWithValue, dispatch }) => {
    try {
      await createApiRequest<{ id: string }>(`/stores/${storeId}/reservation-menus`, {
        method: 'POST',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      dispatch(fetchReservationMenus(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const updateReservationMenu = createAsyncThunk<
  void,
  { storeId: string; menuId: string; input: ReservationMenuInput }
>(
  'reservationSettings/updateReservationMenu',
  async ({ storeId, menuId, input }, { rejectWithValue, dispatch }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservation-menus/${menuId}`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      dispatch(fetchReservationMenus(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const deleteReservationMenu = createAsyncThunk<
  void,
  { storeId: string; menuId: string }
>(
  'reservationSettings/deleteReservationMenu',
  async ({ storeId, menuId }, { rejectWithValue, dispatch }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservation-menus/${menuId}`, {
        method: 'DELETE',
        requiresAuth: true,
      });
      dispatch(fetchReservationMenus(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// ---------- 質問項目 ----------

export const fetchInquiries = createAsyncThunk<ReservationInquiry[], string>(
  'reservationSettings/fetchInquiries',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<ReservationInquiry[]>(
        `/stores/${storeId}/reservation-inquiries`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const createInquiry = createAsyncThunk<
  void,
  { storeId: string; input: ReservationInquiryInput }
>('reservationSettings/createInquiry', async ({ storeId, input }, { rejectWithValue, dispatch }) => {
  try {
    await createApiRequest<{ id: string }>(`/stores/${storeId}/reservation-inquiries`, {
      method: 'POST',
      requiresAuth: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    dispatch(fetchInquiries(storeId));
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const updateInquiry = createAsyncThunk<
  void,
  { storeId: string; inquiryId: string; input: ReservationInquiryInput }
>(
  'reservationSettings/updateInquiry',
  async ({ storeId, inquiryId, input }, { rejectWithValue, dispatch }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservation-inquiries/${inquiryId}`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      dispatch(fetchInquiries(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const deleteInquiry = createAsyncThunk<void, { storeId: string; inquiryId: string }>(
  'reservationSettings/deleteInquiry',
  async ({ storeId, inquiryId }, { rejectWithValue, dispatch }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservation-inquiries/${inquiryId}`, {
        method: 'DELETE',
        requiresAuth: true,
      });
      dispatch(fetchInquiries(storeId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// ---------- 決済方針 ----------

// 未設定店舗は 404 を返すため null を許容する
export const fetchPaymentSetting = createAsyncThunk<PaymentSetting | null, string>(
  'reservationSettings/fetchPaymentSetting',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<PaymentSetting>(`/stores/${storeId}/payment-setting`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      const message = (error as Error).message;
      // 404（未設定）はエラー扱いにしない
      if (message.includes('404')) return null;
      return rejectWithValue(message);
    }
  },
);

export const savePaymentSetting = createAsyncThunk<
  PaymentSetting,
  { storeId: string; setting: PaymentSetting }
>('reservationSettings/savePaymentSetting', async ({ storeId, setting }, { rejectWithValue }) => {
  try {
    await createApiRequest(`/stores/${storeId}/payment-setting`, {
      method: 'PUT',
      requiresAuth: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(setting),
    });
    return setting;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

// Stripe Connect 連携ステータス（未連携時は /manage/store への誘導を表示する）
export const fetchStripeStatus = createAsyncThunk<StripeConnectionStatus, string>(
  'reservationSettings/fetchStripeStatus',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<StripeConnectionStatus>(`/stores/${storeId}/stripe`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// ---------- メール文プレビュー ----------

/**
 * お客様へ送られるメール（受付/確定/キャンセル/リマインダー）の実テンプレプレビューを取得する。
 * ダイアログ内で使い捨てるだけで Redux に保持しないため thunk にはしない。
 */
export const getReservationMailPreview = (
  storeId: string,
  type: ReservationMailPreviewType,
): Promise<ReservationMailPreview> =>
  createApiRequest<ReservationMailPreview>(
    `/stores/${storeId}/reservation-mail-preview?type=${type}`,
    { method: 'GET', requiresAuth: true },
  );

// ---------- 通知メール・店舗情報 ----------

export const fetchNotificationEmails = createAsyncThunk<{ emails: string[] }, string>(
  'reservationSettings/fetchNotificationEmails',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<{ storeId: string; emails: string[] }>(
        `/stores/${storeId}/notification-emails`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const saveNotificationEmails = createAsyncThunk<
  { emails: string[] },
  { storeId: string; emails: string[] }
>(
  'reservationSettings/saveNotificationEmails',
  async ({ storeId, emails }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/notification-emails`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails }),
      });
      return { emails };
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// 未設定店舗は 404 を返すため null を許容する
export const fetchReservationStoreInfo = createAsyncThunk<ReservationStoreInfo | null, string>(
  'reservationSettings/fetchReservationStoreInfo',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<ReservationStoreInfo>(`/stores/${storeId}/store-info`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      const message = (error as Error).message;
      if (message.includes('404')) return null;
      return rejectWithValue(message);
    }
  },
);

// ---------- 予約画像（コース画像・店舗写真。フィードバック2） ----------

/**
 * 予約画像を presigned PUT でアップロードし、公開 URL を返す（メニュー画像と同方式）。
 * リサイズ→presign 取得→S3 PUT。公開 URL は BE が返す publicUrl をそのまま使う
 * （FE 側で URL を組み立てない・契約 A）。
 * アップロード後は各保存 API（コース保存 / reservation-store-images PUT）に URL を含めて確定させる。
 */
export const uploadReservationImage = async (
  storeId: string,
  file: File,
  kind: ReservationImageKind,
): Promise<string> => {
  const resized = await resizeImage(file);
  // リサイズで JPEG 化するためファイル名は uuid.jpg に正規化する（S3 キーの uuid は BE が別途付与）
  const filename = `${uuidv4()}.jpg`;
  const presign = await createApiRequest<{ signedUrl: string; publicUrl: string }>(
    `/stores/${storeId}/reservation-image/presign?filename=${encodeURIComponent(
      filename,
    )}&contentType=${encodeURIComponent('image/jpeg')}&kind=${kind}`,
    { method: 'GET', requiresAuth: true },
  );
  if (!presign?.signedUrl || !presign?.publicUrl) {
    throw new Error('画像アップロード用 URL の取得に失敗しました。');
  }
  const putResponse = await fetch(presign.signedUrl, {
    method: 'PUT',
    body: resized,
    headers: { 'Content-Type': 'image/jpeg' },
  });
  if (!putResponse.ok) {
    throw new Error(`画像のアップロードに失敗しました。(status: ${putResponse.status})`);
  }
  return presign.publicUrl;
};

/** 店舗写真の初期値（未設定店舗・旧 BE の 404 時） */
export const EMPTY_STORE_IMAGES: ReservationStoreImages = {
  heroImageUrl: null,
  galleryImageUrls: [],
};

export const fetchReservationStoreImages = createAsyncThunk<ReservationStoreImages, string>(
  'reservationSettings/fetchReservationStoreImages',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<ReservationStoreImages>(
        `/stores/${storeId}/reservation-store-images`,
        { method: 'GET', requiresAuth: true },
      );
    } catch (error) {
      // 未設定店舗（または未対応の旧 BE）の 404 は空の初期値で表示する
      if (isNotFound(error)) return EMPTY_STORE_IMAGES;
      return rejectWithValue((error as Error).message);
    }
  },
);

export const saveReservationStoreImages = createAsyncThunk<
  ReservationStoreImages,
  { storeId: string; images: ReservationStoreImages }
>(
  'reservationSettings/saveReservationStoreImages',
  async ({ storeId, images }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/reservation-store-images`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(images),
      });
      return images;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const saveReservationStoreInfo = createAsyncThunk<
  ReservationStoreInfo,
  { storeId: string; info: ReservationStoreInfo }
>(
  'reservationSettings/saveReservationStoreInfo',
  async ({ storeId, info }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/store-info`, {
        method: 'PUT',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(info),
      });
      return info;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);
